import type { CSSProperties } from "react";
import type { ResolvedPadding } from "../shared/types";
import {
  escapeHtmlForMso,
  formatPaddingValue,
  parseNumericStyle,
  toCssString,
} from "../shared/utils";
import {
  FONT,
  VML_BORDER_RADIUS,
  calculateArcsize,
  calculateAverageBorderRadius,
  calculateButtonHeight,
  calculateButtonWidth,
  normalizeLineHeight,
} from "./vmlHelpers";

export const buildVmlButton = ({
  linkStyles,
  padding,
  text,
  href,
}: {
  linkStyles: CSSProperties;
  padding: ResolvedPadding;
  text: unknown;
  href: string;
}): { startComment: string; endComment: string } => {
  const averageBorderRadius = calculateAverageBorderRadius(linkStyles);

  const fontSize = parseNumericStyle(linkStyles.fontSize, FONT.DEFAULT_SIZE);
  const lineHeight = normalizeLineHeight(linkStyles.lineHeight, fontSize);

  const buttonWidth = calculateButtonWidth(text, fontSize, padding);
  const buttonHeight = calculateButtonHeight(padding, lineHeight, fontSize);

  const rawArcsize = calculateArcsize(averageBorderRadius, buttonHeight);
  const arcsize = Math.min(rawArcsize, VML_BORDER_RADIUS.MAX_ARCSIZE_PERCENT);

  const backgroundColor = toCssString(linkStyles.backgroundColor);

  const borderWidth = parseNumericStyle(linkStyles.borderWidth);
  const borderColor = toCssString(linkStyles.borderColor || backgroundColor);

  const color = toCssString(linkStyles.color);

  const isOutlinedButton =
    borderWidth > 0 &&
    (!backgroundColor ||
      backgroundColor === "transparent" ||
      backgroundColor === "#ffffff" ||
      backgroundColor === "#FFFFFF");

  const fontFamily = toCssString(linkStyles.fontFamily);
  const fontWeight = toCssString(linkStyles.fontWeight);
  const textDecoration = toCssString(linkStyles.textDecoration, "none");

  const strokeAttr =
    borderWidth > 0
      ? `stroke="t" strokecolor="${borderColor}" strokeweight="${borderWidth}px"`
      : `stroke="f"`;

  const fillAttr = isOutlinedButton ? `` : `fillcolor="${backgroundColor}"`;

  const msoPaddingAlt = formatPaddingValue(padding);
  (linkStyles as Record<string, unknown>)["mso-padding-alt"] = msoPaddingAlt;
  (linkStyles as Record<string, unknown>)["mso-border-alt"] = "none";

  const vmlStyle = `height:${buttonHeight}px;width:${buttonWidth}px;v-text-anchor:middle;`;

  const centerStyle = [
    `color:${color}`,
    `font-family:${fontFamily}`,
    `font-size:${fontSize}px`,
    `font-weight:${fontWeight}`,
    `text-decoration:${textDecoration}`,
    `display:block`,
    `margin:0`,
    `padding:0`,
  ].join(";");

  const textToRender = escapeHtmlForMso(text);

  const startComment = `<!--[if mso]>
<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${href}" style="${vmlStyle}" arcsize="${arcsize}%" ${strokeAttr} ${fillAttr}>
<w:anchorlock/>
<center style="${centerStyle}">${textToRender}</center>
</v:roundrect>
<![endif]--><!--[if !mso]><!-->`;

  const endComment = `<!--<![endif]-->`;

  return { startComment, endComment };
};
